"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { Question, Recommencer, Resultat, remplir, useParcours } from "./Journey";
import { ECHEANCES, plan } from "@/lib/tools/gettingStarted";

/** Les jours d'échéance du module, écrits en français : leur version anglaise. */
const JOURS_EN = { "31 décembre": "31 December", "30 septembre": "30 September" };

/**
 * Les valeurs d'une échéance, mises en forme dans la langue de la page.
 *
 * En anglais britannique (« 1 September 2027 ») plutôt qu'avec dateLisible,
 * qui écrit à l'américaine : la pédagogie de la même page date à l'anglaise.
 */
function valeursEcheance(cle, lang) {
    const v = { ...ECHEANCES[cle] };
    for (const k of ["depuis", "emission"]) {
        if (!v[k]) continue;
        const date = new Date(v[k]).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
            timeZone: "UTC",
        });
        v[k] = lang === "fr" ? date.replace(/^1 /, "1er ") : date;
    }
    if (v.jour && lang !== "fr") v.jour = JOURS_EN[v.jour] || v.jour;
    return v;
}

/**
 * « Par quoi commencer » : le parcours qui met les vingt-six outils dans l'ordre.
 *
 * IL NE REFAIT LE TRAVAIL D'AUCUN OUTIL. Chaque étape renvoie à celui qui la
 * traite, et ne dit que deux choses : pourquoi elle vient là, et quand elle
 * expire. Poser ici les questions que le simulateur d'ACRE pose déjà ferait
 * répondre deux fois à la même chose.
 *
 * QUATRE QUESTIONS, ET CHACUNE RETIRE OU AJOUTE DES ÉTAPES. Une question dont la
 * réponse ne change pas le plan n'aurait rien à faire dans un parcours qui
 * promet un chemin personnalisé.
 */

const PHASES_ECRAN = {
    avancement: 0,
    clients: 1,
    produits: 1,
    local: 1,
    resultat: 2,
};

/** L'icône de chaque étape, prise au registre quand l'outil existe. */
const ICONE_ETAPE = {
    regime: "chart-2-linear",
    revenu: "wallet-money-linear",
    acreDroit: "gift-linear",
    adresse: "home-smile-linear",
    ape: "tag-linear",
    guichet: "buildings-2-linear",
    nonCondamnation: "clipboard-check-linear",
    conjoint: "users-group-two-rounded-linear",
    demandeAcre: "gift-linear",
    cfe: "bill-list-linear",
    assurances: "shield-check-linear",
    mediateur: "chat-round-line-linear",
    rep: "checklist-minimalistic-linear",
    devis: "document-add-linear",
    facture: "bill-check-linear",
    tva: "graph-up-linear",
    vfl: "scale-linear",
    retraite: "hourglass-linear",
};

const L = {
    fr: {
        phases: ["Votre projet", "Votre activité", "Votre plan"],
        retour: "Retour",
        recommencer: "Refaire le parcours",

        qAvancement: "Où en êtes-vous exactement ?",
        qAvancementAide:
            "Cette réponse décide de tout le reste : ce qui est encore devant vous, ce qui est déjà fait, et surtout les deux échéances qui courent dès votre déclaration.",
        idee: "J'y réfléchis encore",
        ideeDetail: "Rien n'est déclaré, tout est ouvert",
        declare: "Je viens de déclarer",
        declareDetail: "Le dossier est parti, j'attends mon SIRET",
        immatricule: "Je suis immatriculé",
        immatriculeDetail: "J'ai mon SIRET, je veux vérifier ce que j'ai oublié",

        qClients: "Qui sont vos clients ?",
        qClientsAide:
            "Vendre à des particuliers ajoute deux obligations, souvent oubliées la première année, que la vente aux entreprises n'entraîne pas.",
        particuliers: "Des particuliers",
        particuliersDetail: "Le grand public",
        professionnels: "Des entreprises",
        professionnelsDetail: "Uniquement des professionnels",
        deux: "Les deux",
        deuxDetail: "Particuliers et entreprises",

        qProduits: "Vendez-vous des produits physiques ?",
        qProduitsAide:
            "Vendre un produit emballé, ou simplement expédier un colis, vous fait entrer dans la filière des emballages. Une prestation sans objet à livrer n'y entre jamais.",
        produitsAucun: "Non, rien de physique",
        produitsAucunDetail: "Services, fichiers, formations",
        produitsSansExpedition: "Oui, remis en main propre",
        produitsSansExpeditionDetail: "En boutique, sur un marché, sur place",
        produitsAvecExpedition: "Oui, et j'expédie",
        produitsAvecExpeditionDetail: "En colis, quelle que soit la marque dedans",

        qLocal: "Où sera l'adresse de votre entreprise ?",
        qLocalAide:
            "C'est l'adresse que le guichet unique demande. Selon le cas, il faut un papier signé, ou rien du tout.",
        chezMoi: "Chez moi",
        chezMoiDetail: "Propriétaire ou locataire",
        chezUnProche: "Chez un proche",
        chezUnProcheDetail: "Hébergé par quelqu'un d'autre",
        localPro: "À une adresse louée",
        localProDetail: "Bail commercial, coworking, société de domiciliation",

        resultatTitre: "Votre plan",
        planTitre: "{n} étapes, dans cet ordre",
        planTitreUne: "Une seule étape vous reste",
        premiere: "Commencez par là",
        phaseAvant: "Tant que rien n'est déclaré",
        phaseCreation: "Au moment de déclarer",
        phaseApres: "Une fois le dossier parti",

        ouvrirGuichet: "Aller sur le guichet unique",

        echeanceAcre: "Dans les {jours} jours suivant votre début d'activité déclaré. Passé ce délai, l'aide est perdue pour de bon.",
        echeanceCfe: "Avant le {jour} de votre année de création. Vous ne paierez pas de CFE cette année-là, mais c'est cet imprimé qui fixe votre base pour les suivantes.",
        echeanceVfl: "Avant le {jour} pour une application l'année suivante. L'option ne se prend jamais en cours d'année.",
        echeanceFacturation: "Depuis le {depuis}, vous devez pouvoir recevoir des factures électroniques. Vous devrez émettre les vôtres à partir du {emission}.",

        franchiesTitre: "Ce que vous avez déjà derrière vous",
        franchiesAide:
            "À votre stade, ces étapes devraient être faites. Si l'une vous a échappé, c'est le moment de la rattraper.",

        ecarteesTitre: "Ce qui ne vous concerne pas",
        ecarteeLocalPro: "L'attestation d'hébergement : votre bail ou votre contrat de domiciliation tient lieu de justificatif.",
        ecarteeProfessionnelsSeulement: "Le médiateur de la consommation : il n'est obligatoire que pour qui vend à des particuliers.",
        ecarteeAucunProduit: "La REP emballages : sans produit physique vendu à des particuliers, aucun emballage n'est mis sur le marché.",

        etapes: {
            regime: ["Savoir jusqu'où la micro peut vous suivre", "Les plafonds ne se dépassent pas impunément : connaissez-les avant de vous lancer, pour ne pas y arriver sans l'avoir vu venir."],
            revenu: ["Savoir ce qu'il vous restera vraiment", "Entre le chiffre d'affaires encaissé et ce que vous gardez, quatre prélèvements. C'est ce chiffre-là qui décide si le projet tient."],
            acreDroit: ["Vérifier votre droit à l'ACRE", "Elle divise vos cotisations la première année, mais elle ne s'obtient plus qu'en la demandant, et le délai court dès la déclaration."],
            adresse: ["Régler l'adresse de l'entreprise", "Chez vous, personne n'a à vous autoriser. Chez un proche, il faut son attestation et son justificatif de domicile."],
            ape: ["Décrire votre activité avec les bons mots", "L'Insee attribue le code APE d'après la description que vous donnez au guichet : une description précise vous évite une correction plus tard."],
            guichet: ["Déposer votre déclaration", "C'est la seule étape que nous ne faisons pas à votre place : elle se dépose sur le guichet unique, et nulle part ailleurs."],
            nonCondamnation: ["Rédiger votre déclaration de non-condamnation", "Le guichet la réclame à toute création. Aucun Cerfa n'existe : c'est une lettre libre dont trois lignes de travers font refuser un dossier."],
            conjoint: ["Vérifier l'attestation au conjoint", "Elle n'est exigée que d'un entrepreneur marié sous un régime de communauté. Deux questions suffisent à savoir de quel côté vous êtes."],
            demandeAcre: ["Déposer votre demande d'ACRE", "L'imprimé rempli, signé, avec la liste des pièces à joindre."],
            cfe: ["Déposer votre déclaration initiale de CFE", "L'imprimé officiel, rempli case par case."],
            assurances: ["Faire le point sur vos assurances", "Cinq obligations possibles, et une seule question par écran pour savoir lesquelles vous visent. La décennale se souscrit avant le premier chantier."],
            mediateur: ["Désigner un médiateur de la consommation", "Obligatoire dès que vous vendez à des particuliers. L'oubli le plus fréquent vient après la désignation : ses coordonnées doivent figurer sur votre site."],
            rep: ["Vérifier vos obligations d'emballages", "Vous croyez peut-être ne pas être concerné parce que vous ne fabriquez rien. Le carton dans lequel vous expédiez est pourtant le vôtre."],
            devis: ["Préparer vos devis et vos factures", "Les mentions obligatoires, la mention de franchise, et les huit activités où le devis est imposé par la loi."],
            facture: ["Vous préparer à la facturation électronique", "Trois obligations que presque tous les articles mélangent, et la franchise en base n'en dispense d'aucune."],
            tva: ["Surveiller vos seuils de TVA", "Deux seuils, deux dates d'effet qui n'ont rien à voir : le majoré vous rend redevable le jour même."],
            vfl: ["Décider du versement libératoire", "Un impôt forfaitaire prélevé avec vos cotisations. Il n'est gagnant que dans certains cas, et l'option est irrévocable pour l'année."],
            retraite: ["Vérifier vos trimestres de retraite", "Un chiffre d'affaires trop faible ne valide pas quatre trimestres, et ceux qui manquent ne se rattrapent pas."],
        },
    },
    en: {
        phases: ["Your project", "Your activity", "Your plan"],
        retour: "Back",
        recommencer: "Start over",

        qAvancement: "Where exactly do you stand?",
        qAvancementAide:
            "This answer drives everything else: what is still ahead of you, what is already done, and above all the two deadlines that start running from your declaration.",
        idee: "Still thinking about it",
        ideeDetail: "Nothing declared yet, everything is open",
        declare: "I have just declared",
        declareDetail: "The file has been sent, I am waiting for my SIRET",
        immatricule: "I am registered",
        immatriculeDetail: "I have my SIRET, I want to check what I missed",

        qClients: "Who are your clients?",
        qClientsAide:
            "Selling to consumers adds two duties, often missed in the first year, that selling to businesses does not.",
        particuliers: "Consumers",
        particuliersDetail: "The general public",
        professionnels: "Businesses",
        professionnelsDetail: "Professionals only",
        deux: "Both",
        deuxDetail: "Consumers and businesses",

        qProduits: "Do you sell physical products?",
        qProduitsAide:
            "A packaged product, or simply a parcel you ship, brings you into the packaging scheme. A service with nothing to deliver never does.",
        produitsAucun: "No, nothing physical",
        produitsAucunDetail: "Services, files, training",
        produitsSansExpedition: "Yes, handed over in person",
        produitsSansExpeditionDetail: "In a shop, at a market, on site",
        produitsAvecExpedition: "Yes, and I ship them",
        produitsAvecExpeditionDetail: "In parcels, whatever brand is inside",

        qLocal: "Where will your business address be?",
        qLocalAide:
            "This is the address the single window asks for. Depending on the case, it needs a signed paper, or nothing at all.",
        chezMoi: "At my home",
        chezMoiDetail: "Owner or tenant",
        chezUnProche: "At someone else's home",
        chezUnProcheDetail: "Hosted by another person",
        localPro: "At a rented address",
        localProDetail: "Commercial lease, coworking, domiciliation company",

        resultatTitre: "Your plan",
        planTitre: "{n} steps, in this order",
        planTitreUne: "One step left",
        premiere: "Start here",
        phaseAvant: "While nothing is declared",
        phaseCreation: "When you declare",
        phaseApres: "Once the file is in",

        ouvrirGuichet: "Go to the single window",

        echeanceAcre: "Within {jours} days of your declared start of activity. After that, the relief is lost for good.",
        echeanceCfe: "Before {jour} of your year of creation. You will pay no business property tax that year, but this form sets your base for the following ones.",
        echeanceVfl: "Before {jour}, to apply the following year. The option is never taken mid-year.",
        echeanceFacturation: "Since {depuis}, you must be able to receive electronic invoices. You will have to issue your own from {emission}.",

        franchiesTitre: "What is already behind you",
        franchiesAide:
            "At your stage, these steps should already be done. If one slipped through, now is the time to catch up on it.",

        ecarteesTitre: "What does not concern you",
        ecarteeLocalPro: "The hosting attestation: your lease or domiciliation contract serves as proof of address.",
        ecarteeProfessionnelsSeulement: "The consumer mediator: it is only compulsory for those selling to consumers.",
        ecarteeAucunProduit: "Packaging producer duties: with no physical product sold to consumers, no packaging is placed on the market.",

        etapes: {
            regime: ["Know how far the micro scheme can take you", "Ceilings are not crossed without consequence: know them before you start, so you do not reach one without seeing it coming."],
            revenu: ["Find out what you will actually keep", "Between the turnover you collect and what you keep, four deductions. That figure decides whether the project holds."],
            acreDroit: ["Check your entitlement to ACRE", "It halves your contributions in the first year, but it is only granted on application, and the clock starts at your declaration."],
            adresse: ["Settle the business address", "At your own home, nobody has to authorise you. At someone else's, you need their attestation and their proof of address."],
            ape: ["Describe your activity in the right words", "INSEE assigns the APE code from the description you give at registration: a precise description saves you a correction later."],
            guichet: ["File your declaration", "This is the one step we do not do for you: it is filed on the single window, and nowhere else."],
            nonCondamnation: ["Write your non-conviction declaration", "The single window requires it for every creation. No official form exists: it is a free-form letter where three wrong lines get a file refused."],
            conjoint: ["Check the spouse attestation", "It is only required of an entrepreneur married under a community property regime. Two questions tell you which side you are on."],
            demandeAcre: ["File your ACRE application", "The form filled in, signed, with the list of documents to attach."],
            cfe: ["File your initial business property tax return", "The official form, filled in box by box."],
            assurances: ["Take stock of your insurance", "Five possible duties, and one question per screen to find out which ones reach you. Ten-year cover is taken out before the first site opens."],
            mediateur: ["Appoint a consumer mediator", "Compulsory as soon as you sell to consumers. The most common oversight comes after the appointment: their details must appear on your site."],
            rep: ["Check your packaging duties", "You may believe you are out of scope because you make nothing. The cardboard you ship in is yours all the same."],
            devis: ["Prepare your quotations and invoices", "The mandatory particulars, the exemption notice, and the eight activities where a written quotation is required by law."],
            facture: ["Get ready for electronic invoicing", "Three duties that almost every article mixes up, and the VAT base exemption excuses you from none of them."],
            tva: ["Watch your VAT thresholds", "Two thresholds, two effective dates that have nothing to do with each other: the higher one makes you liable the very same day."],
            retraite: ["Check your pension quarters", "Turnover that is too low does not validate four quarters, and the missing ones cannot be made up later."],
            vfl: ["Decide on the flat-rate income tax option", "A flat tax collected alongside your contributions. It only wins in certain cases, and the option is irrevocable for the year."],
        },
    },
};

/** Une étape du plan : ce qu'elle est, quand elle expire, où elle se fait. */
function Etape({ etape, t, lang, outils, rang, premiere }) {
    const [titre, texte] = t.etapes[etape.cle] || [etape.cle, ""];
    const outil = etape.outil ? outils[etape.outil] : null;
    const echeance = etape.echeance
        ? remplir(
              {
                  acre: t.echeanceAcre,
                  cfe: t.echeanceCfe,
                  vfl: t.echeanceVfl,
                  facturation: t.echeanceFacturation,
              }[etape.echeance],
              valeursEcheance(etape.echeance, lang),
          )
        : null;

    return (
        <div
            className={`rounded-2xl border p-5 sm:p-6 ${
                premiere
                    ? "border-primary/40 bg-primary/[0.04]"
                    : "border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03]"
            }`}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold tabular-nums ${
                        premiere
                            ? "bg-[#D04515] text-white"
                            : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-lightBlue"
                    }`}
                >
                    {rang}
                </div>
                <div className="min-w-0">
                    {premiere && (
                        <div className="text-[11px] font-semibold text-[#D04515] dark:text-[#FF8A5B] uppercase tracking-wider mb-1">
                            {t.premiere}
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <SolarIcon
                            name={ICONE_ETAPE[etape.cle] || "target-linear"}
                            className="text-lg shrink-0 text-gray-400 dark:text-lightBlue/60"
                        />
                        <div className="font-bold dark:text-white leading-snug">{titre}</div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{texte}</p>

                    {echeance && (
                        <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 mb-0">
                            {echeance}
                        </p>
                    )}

                    {outil ? (
                        <Link
                            href={{ pathname: "/tools/[slug]", params: { slug: outil.slug } }}
                            className="inline-block text-sm font-semibold text-primary hover:!text-primary mt-3"
                        >
                            {outil.nom} →
                        </Link>
                    ) : etape.lien ? (
                        <a
                            href={etape.lien}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block text-sm font-semibold text-primary hover:!text-primary mt-3"
                        >
                            {t.ouvrirGuichet} →
                        </a>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default function JeMeLance({ lang = "fr", outils = {} }) {
    const t = L[lang] || L.fr;

    const [r, setR] = useState({});
    const { etape, sens, reduit, aller, retour, recommencer, communs } = useParcours(
        "avancement",
        () => setR({}),
        PHASES_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const p = useMemo(() => plan(r), [r]);

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };

    /** Enregistre une réponse et passe à l'écran suivant. */
    const repondre = (champ, valeur, suivant) => {
        setR((p) => ({ ...p, [champ]: valeur }));
        aller(suivant);
    };

    /** Les trois cartes d'une question, construites depuis les clés de textes. */
    const cartes = (champ, valeurs, icones, suivant) =>
        valeurs.map((v, i) => ({
            icone: icones[i],
            titre: t[v],
            detail: t[`${v}Detail`],
            onClick: () => repondre(champ, v, suivant),
        }));

    if (etape === "avancement") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES_ECRAN.avancement}
                titre={t.qAvancement}
                aide={t.qAvancementAide}
                choix={cartes(
                    "avancement",
                    ["idee", "declare", "immatricule"],
                    ["lightbulb-linear", "clock-circle-linear", "check-circle-linear"],
                    "clients",
                )}
            />
        );
    }

    if (etape === "clients") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES_ECRAN.clients}
                titre={t.qClients}
                aide={t.qClientsAide}
                choix={cartes(
                    "clients",
                    ["particuliers", "professionnels", "deux"],
                    ["users-group-rounded-linear", "buildings-linear", "widget-4-linear"],
                    "produits",
                )}
            />
        );
    }

    if (etape === "produits") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES_ECRAN.produits}
                titre={t.qProduits}
                aide={t.qProduitsAide}
                choix={["aucun", "sansExpedition", "avecExpedition"].map((v, i) => ({
                    icone: ["close-circle-linear", "shop-linear", "box-linear"][i],
                    titre: t[`produits${v[0].toUpperCase()}${v.slice(1)}`],
                    detail: t[`produits${v[0].toUpperCase()}${v.slice(1)}Detail`],
                    onClick: () => repondre("produits", v, "local"),
                }))}
            />
        );
    }

    if (etape === "local") {
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES_ECRAN.local}
                titre={t.qLocal}
                aide={t.qLocalAide}
                choix={cartes(
                    "local",
                    ["chezMoi", "chezUnProche", "localPro"],
                    ["home-smile-linear", "users-group-two-rounded-linear", "shop-2-linear"],
                    "resultat",
                )}
            />
        );
    }

    if (!p) return null;

    const nomPhase = {
        avant: t.phaseAvant,
        creation: t.phaseCreation,
        apres: t.phaseApres,
    };
    // Le numéro d'une étape se lit dans l'ordre du plan entier, pas dans celui
    // de sa phase : la CFE est la dixième chose à faire, pas la deuxième de
    // l'après-création. Calculé une fois, sans muter quoi que ce soit pendant
    // le rendu.
    const rangs = new Map(p.aFaire.map((e, i) => [e.cle, i + 1]));

    return (
        <Resultat onRetour={retour} retour={t.retour} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            <div className="font-bold text-lg dark:text-white leading-snug mt-2">
                {p.total === 1 ? t.planTitreUne : remplir(t.planTitre, { n: p.total })}
            </div>

            <div className="mt-6 space-y-8">
                {p.phases.map(({ phase, etapes }) => (
                    <section key={phase}>
                        <div className="text-[11px] font-semibold text-gray-600 dark:text-grayDark-400 uppercase tracking-wider mb-3">
                            {nomPhase[phase]}
                        </div>
                        <div className="space-y-3">
                            {etapes.map((e) => (
                                <Etape
                                    key={e.cle}
                                    etape={e}
                                    t={t}
                                    lang={lang}
                                    outils={outils}
                                    rang={rangs.get(e.cle)}
                                    premiere={p.premiere?.cle === e.cle}
                                />
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            {p.franchies.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200/70 dark:border-white/10">
                    <div className="font-semibold dark:text-white mb-2">{t.franchiesTitre}</div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mb-3">
                        {t.franchiesAide}
                    </p>
                    <ul className="space-y-2">
                        {p.franchies.map((e) => {
                            const outil = e.outil ? outils[e.outil] : null;
                            return (
                                <li key={e.cle} className="text-sm">
                                    {outil ? (
                                        <Link
                                            href={{
                                                pathname: "/tools/[slug]",
                                                params: { slug: outil.slug },
                                            }}
                                            className="text-gray-600 dark:text-lightBlue hover:!text-primary"
                                        >
                                            {t.etapes[e.cle]?.[0] || e.cle} →
                                        </Link>
                                    ) : (
                                        <span className="text-gray-600 dark:text-lightBlue">
                                            {t.etapes[e.cle]?.[0] || e.cle}
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {p.ecartees.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200/70 dark:border-white/10">
                    <div className="font-semibold dark:text-white mb-3">{t.ecarteesTitre}</div>
                    <ul className="space-y-2">
                        {p.ecartees.map((e) => (
                            <li
                                key={e.cle}
                                className="text-sm text-gray-600 dark:text-lightBlue flex gap-2.5"
                            >
                                <SolarIcon
                                    name="close-circle-linear"
                                    className="text-base shrink-0 mt-0.5 text-gray-400 dark:text-lightBlue/60"
                                />
                                <span>
                                    {
                                        {
                                            localPro: t.ecarteeLocalPro,
                                            professionnelsSeulement:
                                                t.ecarteeProfessionnelsSeulement,
                                            aucunProduit: t.ecarteeAucunProduit,
                                        }[e.motif]
                                    }
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <Recommencer onClick={recommencer} libelle={t.recommencer} />
        </Resultat>
    );
}
